'use client'

import type { ReactNode } from 'react'
import Image from '@/components/Image'
import { useTranslation } from '@/contexts/I18nContext'
import type { NotificationListItem } from '@/lib/api/notification'
import { formatNotificationTime } from '@/lib/utils/notification'
import { renderUnreadDot } from './FeedCardParts'
import {
  type NotificationVariant,
  notificationPlaceholder,
  notificationSurfaceCard,
  notificationText,
} from './styles'

export type InteractionNotificationCardProps = {
  variant: NotificationVariant
  item: NotificationListItem
  onActorClick: () => void
  onNpcClick: () => void
}

type Props = InteractionNotificationCardProps

function getActionLabel(item: NotificationListItem, t: (key: string) => string) {
  if (item.bizType === 'NPC_LIKED') {
    return t('Message.Notification.LikedCharacter')
  }
  if (item.bizType === 'NPC_FAVORITED') {
    return t('Message.Notification.FavoritedCharacter')
  }
  if (item.body) return item.body
  return item.title
}

function getActorName(item: NotificationListItem, t: (key: string) => string) {
  if (item.actorUsername) return item.actorUsername
  return t('Message.Notification.DeletedUser')
}

function getQuote(body: string | null) {
  if (!body) return null
  const text = body.trim()
  if (!text) return null
  return text
}

function renderActorAvatar(src: string | null): ReactNode {
  if (!src) return null
  return <Image src={src} alt="" width={108} height={108} className="size-full object-cover" />
}

function renderQuoteLine(quote: string | null): ReactNode {
  if (!quote) return null
  return (
    <span className="mt-[calc(4*var(--vw))] flex w-full items-start gap-[calc(6*var(--vw))]">
      <span className="mt-[calc(2*var(--vw))] h-[calc(16*var(--vw))] w-px shrink-0 bg-[rgba(255,255,255,0.2)]" />
      <span
        className={`min-w-0 truncate text-[calc(12*var(--vw))] font-normal leading-[calc(20*var(--vw))] ${notificationText.primary}`}
      >
        {quote}
      </span>
    </span>
  )
}

const npcThumbClass = `flex size-[calc(54*var(--vw))] shrink-0 overflow-hidden rounded-[calc(8*var(--vw))] ${notificationPlaceholder}`

function renderNpcImage(src: string | null): ReactNode {
  if (!src) return null
  return (
    <Image
      src={src}
      alt=""
      width={108}
      height={108}
      className="size-full object-cover object-top"
    />
  )
}

/** Interactions tab — only avatars navigate; card body does not open detail. */
export function InteractionNotificationCard({ variant, item, onActorClick, onNpcClick }: Props) {
  const { t } = useTranslation('message')
  const quote = getQuote(item.body)
  const actorName = getActorName(item, t)
  const canOpenActor = Boolean(item.actorUserId)
  const canOpenNpc = Boolean(item.npcId)

  return (
    <div
      className={`relative flex w-full items-start gap-[calc(12*var(--vw))] ${notificationSurfaceCard(variant)} p-[calc(12*var(--vw))]`}
    >
      {renderUnreadDot(!item.read)}
      {canOpenActor ? (
        <button
          type="button"
          className={`size-[calc(54*var(--vw))] shrink-0 overflow-hidden rounded-full ${notificationPlaceholder}`}
          aria-label={actorName}
          onClick={onActorClick}
        >
          {renderActorAvatar(item.actorAvatar)}
        </button>
      ) : (
        <span
          className={`size-[calc(54*var(--vw))] shrink-0 overflow-hidden rounded-full ${notificationPlaceholder}`}
        >
          {renderActorAvatar(item.actorAvatar)}
        </span>
      )}

      <div className="flex min-w-0 flex-1 items-start gap-[calc(12*var(--vw))]">
        <div className="flex min-w-0 flex-1 flex-col items-start pt-[calc(5*var(--vw))]">
          <span
            className={`w-full truncate text-[calc(14*var(--vw))] font-normal leading-[calc(17*var(--vw))] ${notificationText.pure}`}
          >
            {actorName}
          </span>
          <span
            className={`mt-[calc(2*var(--vw))] w-full truncate text-[calc(12*var(--vw))] font-normal leading-[calc(20*var(--vw))] tracking-[-0.04em] ${notificationText.disabled}`}
          >
            {getActionLabel(item, t)}
          </span>
          {renderQuoteLine(quote)}
          <time
            className={`mt-[calc(7*var(--vw))] text-[calc(12*var(--vw))] font-normal leading-[calc(16*var(--vw))] ${notificationText.disabled}`}
          >
            {formatNotificationTime(item.createTime)}
          </time>
        </div>

        {canOpenNpc ? (
          <button
            type="button"
            className={npcThumbClass}
            aria-label={item.npcName || t('Message.Notification.DeletedCharacter')}
            onClick={onNpcClick}
          >
            {renderNpcImage(item.npcAvatar)}
          </button>
        ) : (
          <span className={npcThumbClass}>{renderNpcImage(item.npcAvatar)}</span>
        )}
      </div>
    </div>
  )
}
